import React from "react";
import {
  EmptyState,
  EmptyStateProps,
  LoadPhase,
  SkeletonCard,
  TABLE_BODY_SKELETON,
} from "../../../components/ui";
import { USER_STATUS_STYLES } from "../statusStyles";
import AccountName from "./AccountName";
import type { AdminUser } from "../types/users.types";

/**
 * Which set of columns the table is showing.
 *
 * `roster` is the whole list: what somebody is on, where they are, how
 * recently they opened the app, and where they stand. `expired` is the lapsed
 * accounts, and it asks a different question — not "how are they using it" but
 * "who do we contact, when did it run out, and what can we do about it". So
 * Plan, Location, Joined on and Last active give way to Email and Expired on,
 * and Status gives way to Remind: every row on that tab is lapsed, so a column
 * repeating it for each of them would be a column with one word in it, taking
 * the place of the only action either table offers.
 */
export type UsersTableVariant = "roster" | "expired";

/**
 * How far the reminder for one lapsed account has got.
 *
 * Per row rather than for the table as a whole, because somebody working down
 * the Expired tab sends several, and one shared "sending…" would move under
 * all of them at once.
 */
export type RemindState = "sending" | "sent" | "failed";

/** What the Remind cell says at each of them. */
const REMIND_LABEL: Record<RemindState, string> = {
  sending: "Sending…",
  sent: "Reminded",
  failed: "Try again",
};

interface Column {
  heading: string;
  width: string;
}

const ROSTER_COLUMNS: Column[] = [
  { heading: "Client name", width: "w-[24%]" },
  { heading: "Plan", width: "w-[11%]" },
  { heading: "Location", width: "w-[16%]" },
  { heading: "Joined on", width: "w-[13%]" },
  { heading: "Clients", width: "w-[10%]" },
  { heading: "Last active", width: "w-[12%]" },
  { heading: "Status", width: "w-[14%]" },
];

const EXPIRED_COLUMNS: Column[] = [
  { heading: "Client name", width: "w-[30%]" },
  { heading: "Email", width: "w-[24%]" },
  { heading: "Clients", width: "w-[12%]" },
  { heading: "Expired on", width: "w-[17%]" },
  { heading: "Remind", width: "w-[17%]" },
];

interface UsersTableProps {
  users: AdminUser[];
  /** Stands in for the table body when the list comes back with no rows. */
  empty: EmptyStateProps;
  /** Which columns to draw — see `UsersTableVariant`. */
  variant?: UsersTableVariant;
  /**
   * Where the list is. While it is on its way the body says nothing — no rows
   * and no message — and only a load slow enough to notice shimmers, as one
   * block the size of the table. Rows already in hand are left alone either
   * way: a table with something to show goes on showing it while the next
   * answer is fetched.
   */
  phase?: LoadPhase;
  onSelectUser?: (user: AdminUser) => void;
  /** The Remind column's action. Only ever asked for on the `expired` variant. */
  onRemind?: (user: AdminUser) => void;
  /** Keyed by user id; rows not in it have not been reminded this session. */
  remindStates?: Record<number, RemindState>;
}

const StatusPill: React.FC<{ user: AdminUser }> = ({ user }) => (
  <span
    className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-[12px] font-medium ${
      USER_STATUS_STYLES[user.status]
    }`}
  >
    {user.status}
  </span>
);

/**
 * The Remind column's cell: one link-shaped button, and afterwards what came
 * of it.
 *
 * The click is kept off the row on purpose — the row opens the account, and a
 * reader reaching for "Reactivate" is not asking to leave the list.
 *
 * A reminder already sent stays on screen as a word rather than as a button
 * that can be pressed again: the account has been mailed, and mailing it twice
 * because the first press looked like it did nothing is the mistake this
 * prevents.
 */
const RemindCell: React.FC<{
  user: AdminUser;
  state?: RemindState;
  onRemind?: (user: AdminUser) => void;
}> = ({ user, state, onRemind }) => {
  if (state === "sending" || state === "sent") {
    return <span className="text-[14px] text-ink-muted">{REMIND_LABEL[state]}</span>;
  }

  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onRemind?.(user);
      }}
      className="text-[14px] font-medium text-brand-dark hover:underline focus:outline-none focus-visible:underline"
    >
      {state === "failed" ? REMIND_LABEL.failed : "Reactivate"}
    </button>
  );
};

const UsersTable: React.FC<UsersTableProps> = ({
  users,
  empty,
  variant = "roster",
  phase = "ready",
  onSelectUser,
  onRemind,
  remindStates,
}) => {
  const columns = variant === "expired" ? EXPIRED_COLUMNS : ROSTER_COLUMNS;
  const cellClasses = "px-4 sm:px-6 py-4 text-[14px] text-ink";

  if (phase !== "ready" && users.length === 0) {
    // The table's own area, standing in for the whole list — see
    // `TABLE_BODY_SKELETON`. Hidden from screen readers with the rest of the
    // placeholders; the card above says it is loading in words.
    return (
      <div className="flex-1 min-h-0 p-4 sm:p-6">
        <SkeletonCard phase={phase} className={TABLE_BODY_SKELETON} />
      </div>
    );
  }

  return (
    // The only scroll region on the page: rows move, the sticky header row and
    // the card's pagination footer stay put. `min-h-0` is what lets this shrink
    // inside the card's flex column instead of pushing the footer off-screen.
    <div className="flex-1 min-h-0 overflow-auto custom-scrollbar-overlay">
      {/* Full height only when the rows are gone: an empty table that stops at
          its header leaves the state stranded at the top of the card with a
          screenful of nothing under it, so the body takes the whole scroll
          region and the one cell in it centres in that. With rows to show the
          table sizes to them, or every row would stretch to share the height. */}
      <table
        className={`w-full table-fixed border-collapse text-left ${
          variant === "expired" ? "min-w-[820px]" : "min-w-[900px]"
        } ${users.length === 0 ? "h-full" : ""}`}
      >
        <colgroup>
          {columns.map((column) => (
            <col key={column.heading} className={column.width} />
          ))}
        </colgroup>
        {/* The header pins to the top of this container. Its bottom rule is an
            inset shadow rather than a border because collapsed table borders
            scroll away from a sticky cell. */}
        <thead className="sticky top-0 z-10">
          <tr>
            {columns.map((column) => (
              <th
                key={column.heading}
                scope="col"
                className="bg-white px-4 sm:px-6 py-3 text-[13px] font-normal text-ink-muted shadow-[inset_0_-1px_0_#E9EDF1]"
              >
                {column.heading}
              </th>
            ))}
          </tr>
        </thead>

        {users.length === 0 ? (
          <tbody>
            <tr>
              <td colSpan={columns.length} className="align-middle">
                <EmptyState {...empty} />
              </td>
            </tr>
          </tbody>
        ) : (
          <tbody>
            {users.map((user) => (
              <tr
                key={user.id}
                onClick={() => onSelectUser?.(user)}
                className={`border-b border-line last:border-0 transition-colors ${
                  onSelectUser ? "hover:bg-[#F8FEFF] cursor-pointer" : ""
                }`}
              >
                <td className="px-4 sm:px-6 py-4">
                  <AccountName name={user.name} />
                </td>

                {variant === "expired" ? (
                  <>
                    <td className={`${cellClasses} truncate`}>
                      {user.email || "—"}
                    </td>
                    <td className={cellClasses}>{user.clients ?? "NA"}</td>
                    <td className={`${cellClasses} whitespace-nowrap`}>
                      {user.expiresOn ?? "—"}
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <RemindCell
                        user={user}
                        state={remindStates?.[user.id]}
                        onRemind={onRemind}
                      />
                    </td>
                  </>
                ) : (
                  <>
                    <td className={cellClasses}>{user.plan}</td>
                    <td className={`${cellClasses} truncate`}>
                      {user.location}
                    </td>
                    <td className={`${cellClasses} whitespace-nowrap`}>
                      {user.joinedOn}
                    </td>
                    {/* "NA" rather than 0 for an account that has not added a
                        client yet — see the note on `AdminUser.clients`. */}
                    <td className={cellClasses}>{user.clients ?? "NA"}</td>
                    <td className={`${cellClasses} whitespace-nowrap`}>
                      {user.lastActive}
                    </td>
                    <td className="px-4 sm:px-6 py-4">
                      <StatusPill user={user} />
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        )}
      </table>
    </div>
  );
};

export default UsersTable;
